import React, { useEffect, useState } from "react";
import { Row, Col, Alert } from "reactstrap";
import qs from "qs";
import { MdPrint } from "react-icons/md";
import BEMHelper from "react-bem-helper";
import { Trans } from "@lingui/macro";

// Elements
import Button from "@/components/elements/BaseButton";
import Panel from "@/components/elements/Panel";
import Spinner from "@/components/elements/Spinner";
import { createArticlePaymentUrls, getTranslate, tryParseJSONObject } from "@/misc/common";

import "./Completed.css";
import { useAppDispatch, useAppSelector } from "@/hooks";
import StripeCheckout from '@/components/StripeCheckout';
import _StripeCheckout from './StripeCheckout';
import Company from "../partials/Company";
import ArticleSummary from "../partials/ArticleSummary";
import ErrorAlert from "../partials/ErrorAlert";
import { persistor } from "@/store";
import { withPaymentScroll, WithPaymentScrollProps } from "@/hoc/withPaymentScroll";
import { setAccountId } from "@/reducers/stripeSlice";

const c = new BEMHelper({ name: "Completed" });

export const ArticleCompleted: React.FC<WithPaymentScrollProps> = ({ completePaymentPaymentRef, iOSSafari }) => {
    const {
        checkout,
        selected: selectedArticle,
        entity: completedArticle,
    } = useAppSelector((s) => s.article);
    const localize = useAppSelector((s) => s.localize);
    const translate = getTranslate(localize);
    const company = useAppSelector((s) => s.company);
    const configuration = useAppSelector((s) => s.configuration.data);
    const dispatch = useAppDispatch();
    const [completePayment, setCompletePayment] = useState(false);
    const { customerEmail } = useAppSelector((s) => ({
        customerEmail:
            s.user?.data?.CustomerProfile?.Email ||
            s.form?.customerForm?.values?.Customer?.Email,
    }));

    const payWithBillmate = company.data?.BookingSettings?.PaymentProviderId === 3;
    const payWithStripe = company?.data?.BookingSettings?.PaymentProviderId === 5;
    const paymentEnabled = !!company.data?.BookingSettings?.PaymentEnabled;

    useEffect(() => {
        window.onbeforeunload = () => {
            persistor.pause();
            dispatch({ type: 'PURGE_PERSISTED' });
        }

        window.onmessage = (ev) => {
            try {
                const data = tryParseJSONObject(ev.data);
                if (data && data.event === 'purchase_initialized' && checkout?.entity?.Url) {
                    const billmateContainer: HTMLIFrameElement | null = document.querySelector(`[src^="${checkout.entity.Url}"]`);
                    billmateContainer?.contentWindow?.postMessage('purchase_complete', '*');
                }
            } catch (error) {
                // intentionally left blank
            }
        };

        if (paymentEnabled && (iOSSafari || configuration.paymentButton)) {
            setCompletePayment(true);
        }

        if (checkout.entity && payWithStripe && checkout.entity.StripeAccount) {
            dispatch(setAccountId(checkout.entity.StripeAccount));
        } else if (checkout.entity && payWithStripe && checkout.entity.Url && window.top?.location) {
            window.top.location = checkout.entity.Url;
        }
    }, [checkout.entity, payWithBillmate, payWithStripe, paymentEnabled, configuration.paymentButton]);

    if (!selectedArticle || !company.data) return null;

    const companyName = company.data.Name || translate("completed.error.companyNameDefault");
    const completedErrorTemplate = translate("completed.error");
    const completedErrorMessage = completedErrorTemplate
        ? completedErrorTemplate.replace("{companyName}", companyName)
        : `Betalningen kunde inte laddas, vänligen kontakta ${companyName}`;

    if (checkout.error) {
        return (
            <Panel>
                {checkout?.error ? <ErrorAlert error={checkout.error} /> : null}
                <Row>
                    <Col xs={12}>
                        {completedErrorMessage}
                    </Col>
                </Row>
            </Panel>
        );
    }

    return (
        <Panel>
            <div id="section-to-print">
                <h4
                    data-payment-target
                    className="text-left text-danger"
                    style={{ marginBottom: 20, padding: "0.3rem" }}
                >
                    <Trans id="youNeedToPayTheArticle"></Trans>
                </h4>

                {company?.data.BookingSettings?.BookingReceiptMessage && (
                    <p style={{ marginBottom: 20, padding: "0.3rem" }}>
                        {company.data.BookingSettings.BookingReceiptMessage}
                    </p>
                )}
                {checkout.isLoading ? (
                    <div className="hidden-print">
                        <div>
                            <Spinner {...c("paymentLoading")} />
                        </div>
                        <Alert color="warning" className="clearfix">
                            <Trans id="yourArticleMustBePaid"></Trans>
                        </Alert>
                    </div>
                ) : null}
                {checkout.entity?.ClientSecret && !iOSSafari && !configuration.paymentButton && payWithStripe ? (
                    <div key={checkout.entity.SessionId} ref={completePaymentPaymentRef}>
                        <Row className="hidden-print" style={{ marginBottom: "1rem" }}>
                            <Col xs={12}>
                                <StripeCheckout clientSecret={checkout.entity.ClientSecret} />
                            </Col>
                        </Row>
                    </div>
                ) : null}
                {checkout.entity?.Url && payWithBillmate && !iOSSafari && !configuration.paymentButton ? (
                    <iframe
                        ref={completePaymentPaymentRef}
                        src={`${checkout.entity.Url}`}
                        data-testid="billmateContainer"
                        sandbox="allow-top-navigation allow-top-navigation-by-user-activation allow-scripts allow-same-origin allow-forms allow-popups allow-popups-to-escape-sandbox"
                        width="100%"
                        height="900px"
                        frameBorder="0"
                    ></iframe>
                ) : null}
                {completePayment ? (
                    <div ref={completePaymentPaymentRef} {...c('userInitiatedPayment')} >
                        <Button
                            primary
                            onClick={() => {
                                try {
                                    if (window?.top?.location) {
                                        const bokameraSiteUrl = process.env.REACT_APP_BOKAMERA_SITE_URL;
                                        const { confirmationUrl } =
                                            createArticlePaymentUrls(
                                                configuration,
                                                "hosted",
                                                selectedArticle.Id,
                                                customerEmail
                                            );

                                        const searchString = qs.stringify({
                                            referenceId: completedArticle?.Id,
                                            companyId: company?.data?.Id,
                                            email: customerEmail,
                                            paymentProviderId: company.data?.BookingSettings?.PaymentProviderId,
                                            articles: [{
                                                InternalReferenceId: selectedArticle.Id,
                                                ArticleTypeId: selectedArticle.ArticleTypeId,
                                                Quantity: 1
                                            }]
                                        });

                                        let paymentUrl = `${bokameraSiteUrl}/payment?${searchString}`;

                                        if (confirmationUrl) {
                                            paymentUrl = `${paymentUrl}&confirmationUrl=${encodeURIComponent(typeof confirmationUrl === 'string' ? confirmationUrl : confirmationUrl.href)}`
                                        }

                                        window.top.location = paymentUrl;
                                    }
                                } catch (error) {

                                }
                            }}
                        >
                            <Trans id="completePayment" />
                        </Button>
                    </div>
                ) : null}
                {completedArticle ? (
                    <div>
                        <Row {...c("body")}>
                            <Col xs={12} sm={6} md={6} lg={6}>
                                <Company />
                            </Col>
                            <Col xs={12} sm={6} md={6} lg={6}>
                                <ArticleSummary article={selectedArticle} />
                            </Col>
                        </Row>
                        <Row className="hidden-print">
                            <Col xs={12}>
                                <Button
                                    className="button float-right"
                                    onClick={() => {
                                        window.print();
                                    }}
                                >
                                    <MdPrint /> <Trans id="print"></Trans>
                                </Button>
                            </Col>
                        </Row>
                    </div>
                ) : null}
            </div>
        </Panel>
    );
};


export default withPaymentScroll(ArticleCompleted);
